import {
    Alert,
    Box,
    Fade,
    FormControl,
    FormLabel,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EShareBy } from '@sharedInterfaces/globalEnums';
import type { IOrgUnitDetailDTO } from '@sharedInterfaces/IOrgUnit';
import type { ISmallEmployee } from '@sharedInterfaces/IWhoIAm';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import shareViaServer from '@src/APIs/graphQl/Company/shareViaServer';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import EmployeesInput from '@src/App/Components/General/FormularElements/formsControls/inputs/EmployeesInput/EmployeesInput';
import { generateTextForEntity } from '@src/helper/generateTextForEntity';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type { Entity } from '@src/Objects/Entity';
import type { AppState } from '@store/store';
import { Copy, Mail, Send, X } from 'lucide-react';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import CheckMarkBoxDialog from '../../Dialogs/CheckMarkBoxDialog/CheckMarkBoxDialog';
import { useDialog } from '../../Dialogs/DialogManager';
import { useErrorDialog } from '../../Dialogs/ErrorDialog/ErrorDialog';
import RowElement from '../../General/FormularElements/RowElement/RowElement';
import Button from '../Button/Button';

interface ShareButtonProps {
    entity: Entity;
}

/**
 * ShareButton component.
 *
 * @param { entity } - The entity object.
 * @returns { JSX.Element } - The generated JSX element.
 */
export function ShareButton({ entity }: ShareButtonProps) {
    const { openDialog } = useDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).skills.oftenUsed;

    const showDialog = () => openDialog(<ShareDialog id={'ShareDialog'} entity={entity} resolve={() => {}} />);

    return (
        <>
            <IconButton title={langStrings.share} onClick={showDialog}>
                {getAppIcon(EIconCategory.SHARE)}
            </IconButton>
        </>
    );
}

export function ShareMenuItem({ entity }: { entity: Entity }) {
    const { openDialog } = useDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).skills.oftenUsed;

    return (
        <>
            <MenuItem onClick={open}>
                <ListItemIcon>{getAppIcon(EIconCategory.SHARE)}</ListItemIcon>
                <ListItemText>{langStrings.share}</ListItemText>
            </MenuItem>
        </>
    );

    function open(event: React.MouseEvent<HTMLElement>) {
        event.stopPropagation();
        event.preventDefault();
        openDialog(<ShareDialog id={'ShareDialog'} entity={entity} resolve={() => {}} />);
    }
}

interface ShareDialogProps {
    id: string;
    entity: Entity;
    resolve: () => void;
}

/**
 * ShareDialog component.
 *
 * @param {object} close - An object with a "close" function of type "() => void".
 * @returns {JSX.Element} - The JSX Element representing the ShareDialog component.
 */
export function ShareDialog({ id, entity, resolve }: ShareDialogProps) {
    const theme = useTheme();
    const showErrorDialog = useErrorDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).skills.oftenUsed;
    const link = `${window.location.origin}${entity.link}`;
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);
    const [employees, setEmployees] = React.useState<ISmallEmployee[]>([]);
    const [showDetails, setShowDetails] = React.useState(false);
    const [sent, setSent] = React.useState(false);

    const [subject, setSubject] = React.useState('');
    const [body, setBody] = React.useState('');

    const [shareBy, setShareBy] = React.useState<'' | EShareBy>('');

    const [sending, setSending] = React.useState(false);

    useEffect(() => {
        const sub = async () => {
            const { subject, text } = await generateTextForEntity(entity, link);
            setSubject(subject);
            setBody(text);
        };
        sub();
    }, [entity]);

    const onShareByChange = (shareBy: EShareBy) => {
        setShareBy(shareBy);
        setShowDetails(true);
    };

    const onClose = () => {
        resolve();
    };
    const onSend = async () => {
        if (shareBy === '') return;
        setSent(false);
        if (shareBy === EShareBy.teams) {
            setSending(true);
            await shareByTeams(employees, subject, body);
            setTimeout(() => {
                setSending(false);
            }, 3000);
        } else {
            setSending(true);
            await shareViaServer(
                shareBy,
                employees.map((e) => e.id),
                body,
            )
                .then(() => {
                    setSending(false);
                    setSent(true);
                    // onClose();
                })
                .catch((ex) => {
                    showErrorDialog(ex);
                    setTimeout(() => {
                        setSending(false);
                    }, 3000);
                });
        }

        // onClose();
    };
    return (
        <Dialog
            id={id}
            footer={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Button text={langStrings.cancel} icon={<X size={16} />} onClick={onClose} />
                    {shareBy !== '' && (
                        <Button
                            text={sending ? 'Sende...' : langStrings.send}
                            disabled={sending || !employees.length}
                            icon={<Send size={16} />}
                            onClick={onSend}
                        />
                    )}
                </Box>
            }
            title={`${langStrings.share} - ${entity.title}`}
            onClose={onClose}
        >
            <Box sx={{ minHeight: 160, minWidth: 'min(800px, 75vw)' }}>
                <Stack spacing={3}>
                    {/* Link Section */}
                    <RowElement title={langStrings.link}>
                        <TextField
                            fullWidth
                            size="small"
                            value={link}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(link)}
                                        title={langStrings.copy}
                                    >
                                        <Copy size={16} />
                                    </IconButton>
                                ),
                            }}
                        />
                    </RowElement>

                    {/* Share Method Selection */}
                    <Box>
                        <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                            Teilen über
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            {[
                                { type: EShareBy.mail, icon: <Mail size={20} />, label: langStrings.mail },
                                {
                                    type: EShareBy.slack,
                                    icon: getAppIcon(EIconCategory.SLACK),
                                    label: langStrings.slack,
                                },
                                {
                                    type: EShareBy.teams,
                                    icon: getAppIcon(EIconCategory.MS_TEAMS),
                                    label: langStrings.msTeams,
                                },
                            ].map((method) => (
                                <Paper
                                    key={method.type}
                                    elevation={0}
                                    onClick={() => onShareByChange(method.type)}
                                    sx={{
                                        p: 2,
                                        border: `2px solid ${shareBy === method.type ? theme.palette.primary.main : alpha(theme.palette.divider, 0.3)}`,
                                        bgcolor:
                                            shareBy === method.type
                                                ? alpha(theme.palette.primary.main, 0.1)
                                                : 'transparent',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        flex: { xs: 'none', sm: 1 },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1,
                                        '&:hover': {
                                            borderColor: theme.palette.primary.main,
                                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            color:
                                                shareBy === method.type
                                                    ? theme.palette.primary.main
                                                    : theme.palette.text.secondary,
                                        }}
                                    >
                                        {method.icon}
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: shareBy === method.type ? 600 : 400,
                                            color:
                                                shareBy === method.type
                                                    ? theme.palette.primary.main
                                                    : theme.palette.text.primary,
                                        }}
                                    >
                                        {method.label}
                                    </Typography>
                                </Paper>
                            ))}
                        </Stack>
                    </Box>

                    {/* Details Section */}
                    <Fade in={showDetails}>
                        <Stack spacing={2}>
                            {/* Recipients */}
                            <RowElement title={langStrings.shareWithText}>
                                <EmployeesInput
                                    allEmployees={allEmployees}
                                    selectedEmployees={employees}
                                    showMode={false}
                                    size="small"
                                    onChange={setEmployees}
                                />
                            </RowElement>

                            {/* Subject (only for certain share types) */}
                            {shareBy !== EShareBy.mail && shareBy !== EShareBy.slack && shareBy !== '' && (
                                <RowElement title={langStrings.subject}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                    />
                                </RowElement>
                            )}

                            {/* Message */}
                            <RowElement title={langStrings.text}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                />
                            </RowElement>
                        </Stack>
                    </Fade>

                    {/* Success Message */}
                    {sent && (
                        <Alert severity="success" onClose={() => setSent(false)} sx={{ mt: 2 }}>
                            Erfolgreich gesendet!
                        </Alert>
                    )}
                </Stack>
                {sent && <CheckMarkBoxDialog onClose={() => setSent(false)} />}
            </Box>
        </Dialog>
    );
}

/**
 * This function copies the provided link to the clipboard.
 *
 * @param {string} link - The link to be copied to the clipboard.
 *
 * @returns {void}
 */
function copyToClipboard(link: string) {
    navigator.clipboard.writeText(link);
}

/**
 * Generates a description string based on the provided HTML description.
 *
 * @param {string} htmlDescription - The HTML description used to generate the description string.
 * @returns {string} The generated description string.
 */
export function generateDescription(htmlDescription: string, title: string = 'Beschreibung'): string {
    if (htmlDescription !== '') {
        htmlDescription = htmlDescription
            .replace(/\u00A0/g, ' ')
            .replace(/&nbsp;/g, ' ')
            .replace(/\s+/g, ' ')
            .replace(/ {2,}/g, ' ')
            .trim(); // Remove extra whitespace
        return `${title}:
${extractTextFromHTML(htmlDescription.length > 800 ? htmlDescription.substring(0, 800) + '...' : htmlDescription)}
`;
    }
    return '';
}

/**
 * Share by teams.
 *
 * @param entity - The entity object.
 * @param employees - The array of employee objects.
 * @returns A promise that resolves with void.
 */
async function shareByTeams(employees: ISmallEmployee[], subject: string, text: string) {
    const newText = text.replace(/&/g, '(UND)');
    const newSubject = subject.replace(/&/g, '(UND)');
    const emailAddresses = employees.map((e) => e.email).join(',');
    const url = `https://teams.microsoft.com/l/chat/0/0?users=${employees.length ? emailAddresses : ''}&topicName=${window.encodeURI(newSubject)}&message=${window.encodeURI(newText)}`;
    const win = window.open(url, '_blank', 'width=600,height=400');
    if (win) {
        window.focus();
    } else {
        console.log('Das Popup wurde blockiert.');
    }
    setTimeout(() => {
        if (win) win.close();
    }, 10000);
}

/**
 * Extracts text from HTML.
 *
 * @param {string} html - The HTML string to extract text from.
 * @returns {string} The extracted text.
 */
function extractTextFromHTML(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.innerText || tempDiv.textContent || '';
}

/**
 * Check if the given object is an organization unit.
 *
 * @param {any} obj - The object to check.
 * @returns {boolean} - Returns true if the object is an organization unit, otherwise returns false.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isOrgUnit(obj: any): obj is IOrgUnitDetailDTO {
    return obj && typeof obj.leader === 'object' && typeof obj.leader.id === 'number';
}
